/**
 * AutoSaveNavigationGuard - Prevents navigation when autosave is in progress.
 *
 * Bridges useAutoSaveCoordination (which tracks pending/saving state across editors)
 * with useUnsavedChangesGuard (which blocks browser close and React Router navigation).
 *
 * Renders a ConfirmationModal when the user tries to navigate away with unsaved changes.
 * Must be rendered inside an AutoSaveCoordinationProvider.
 */

import { useCallback, useEffect, useState } from 'react';

import { useOverlayOrder } from '@/contexts/OverlayOrderContext';
import { StackingLevelProvider } from '@/contexts/StackingContext';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { ConfirmationDialog } from '@/lib/base/overlays';

import { useAutoSaveCoordination } from './useAutoSaveCoordination';

interface AutoSaveNavigationGuardProps {
  onGuardChange?: (guard: ((action: () => void) => boolean) | null) => void;
}

/**
 * Renders nothing visible unless autosave is in progress and the user tries to navigate.
 * Place inside AutoSaveCoordinationProvider to enable navigation blocking.
 *
 * When `onGuardChange` is provided, the guard function is propagated up whenever
 * auto-save is pending/saving, enabling parent slideouts to intercept push navigation.
 */
export function AutoSaveNavigationGuard({
  onGuardChange,
}: AutoSaveNavigationGuardProps = {}) {
  const coordination = useAutoSaveCoordination();
  // Block navigation when a save is in progress OR has failed: an errored save
  // means the user's latest edits only live in memory (autosave keeps retrying
  // in the background), so leaving now risks losing them.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    return coordination.subscribe(({ hasPendingOrSaving, hasError }) => {
      setHasUnsavedChanges(hasPendingOrSaving || hasError);
    });
  }, [coordination]);

  const handleDiscard = useCallback(() => {
    // No local state to reset — the user chose to navigate away
    // while changes were still unsaved.
  }, []);

  const { guard, modalProps } = useUnsavedChangesGuard({
    hasUnsavedChanges,
    onDiscard: handleDiscard,
  });

  // Propagate guard up to parent while there are unsaved/failed changes.
  // Cleanup calls onGuardChange(null) on unmount so the parent can
  // unregister the guard when this component is torn down.
  useEffect(() => {
    if (hasUnsavedChanges) {
      onGuardChange?.(guard);
    } else {
      onGuardChange?.(null);
    }
    return () => {
      onGuardChange?.(null);
    };
  }, [hasUnsavedChanges, guard, onGuardChange]);

  // This guard can be mounted at the ambient (level 0) StackingContext — e.g.
  // inside the AI Actions detail panel, which isn't wrapped in its own
  // StackingLevelProvider — so ConfirmationDialog's useStackingZIndex() call
  // would otherwise resolve below any already-open CRM record slideout (the
  // global SlideoutRenderer's backdrop is a hardcoded z-[500]; slideouts
  // registered via useOverlayOrder land at z >= 600). Registering the guard
  // itself with useOverlayOrder — the same shared, monotonically-increasing
  // counter every other overlay (slideouts, modals, search, compose) uses —
  // guarantees a level assigned after whatever most recently registered, so
  // it always paints on top regardless of slideout stack depth or churn
  // (e.g. a slideout remounting after being popped and reopened). Falls back
  // to level 0 when the dialog is closed, or when no OverlayOrderProvider is
  // mounted at all (e.g. in isolated tests) — see useOverlayOrder's doc
  // comment.
  const dialogStackingLevel = useOverlayOrder(
    modalProps.isOpen ? 'unsaved-changes-guard' : ''
  );

  return (
    <StackingLevelProvider level={dialogStackingLevel}>
      <ConfirmationDialog
        isOpen={modalProps.isOpen}
        title='Unsaved changes'
        description='Your changes haven’t finished saving. If you leave now, they may be lost.'
        confirmLabel='Leave anyway'
        cancelLabel='Stay'
        variant='destructive'
        onConfirm={modalProps.onDiscard}
        onCancel={modalProps.onCancel}
      />
    </StackingLevelProvider>
  );
}
